import React, { useState } from 'react';
import { useTranslation } from '~/Locale';
import { Button, Message, Caption } from '@kvdbil/components';
import { useForm } from 'react-hook-form';
import PasswordFieldWithControl from '~/App/shared/components/Fields/PasswordFieldWithControl';
import { resetPasswordSubmit } from '~/helpers/orchestration/password';
import { sessionStart } from '~/App/shared/actions/session';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { formErrors } from '~/helpers/validation';
import { useDispatch } from 'react-redux';
import {
  PasswordField,
  MinimumAgeField
} from '~/App/shared/components/Fields/shared';

const Form = styled.form`
  display: grid;
  grid-row-gap: 0.75rem;
`;

interface Props {
  token: string;
  displayTermsCheckbox: boolean;
  redirectTo: string;
}

const ResetPasswordForm = ({
  token,
  displayTermsCheckbox,
  redirectTo
}: Props) => {
  const [isLoading, setLoading] = useState(false);
  const [isCompleted, setCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      password: '',
      passwordConfirm: '',
      termsAccepted: false
    }
  });

  const onSubmit = () => {
    setError(null);
    setLoading(true);
    const values = getValues();

    resetPasswordSubmit({
      token,
      termsAccepted: displayTermsCheckbox ? values.termsAccepted : undefined,
      newPassword: values.password
    })
      .then(response => {
        setLoading(false);
        setCompleted(true);

        dispatch(
          sessionStart({
            auth: {
              email: response.data.member.person.email,
              password: values.password
            }
          })
        );

        history.push(redirectTo);
      })
      .catch((error: Record<string, string>[]) => {
        const errorMessage = formErrors(
          { errors: error },
          'response.data.message',
          true
        );
        setError(errorMessage[0]);
        setLoading(false);
      });
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} noValidate>
      <PasswordField
        control={control}
        t={t}
        isDisabled={isLoading}
        autoComplete="new-password"
      />
      <PasswordFieldWithControl
        control={control}
        name="passwordConfirm"
        isDisabled={isLoading}
        label={t('Confirm password')}
        rules={{
          validate: (value: string) => {
            const password = getValues().password;
            return value === password || t(`Passwords don't match`);
          }
        }}
        autoComplete="new-password"
        required
      />
      {displayTermsCheckbox && (
        <>
          <MinimumAgeField
            control={control}
            t={t}
            name="termsAccepted"
            label={<Caption>{t('I confirm that I am over 16')}</Caption>}
          />
          {errors?.termsAccepted && (
            <Message fullWidth type="error">
              {errors?.termsAccepted?.message}
            </Message>
          )}
        </>
      )}
      <Button color="secondary" fullWidth isLoading={isLoading} type="submit">
        {t('Submit')}
      </Button>

      {error && (
        <Message fullWidth withIcon type="error">
          {error}
        </Message>
      )}
      {isCompleted && !error && (
        <Message fullWidth withIcon type="success">
          {t('Completed')}
        </Message>
      )}
    </Form>
  );
};

export default ResetPasswordForm;
